/**
 * The shape of the one live-game store. Kept in its own module (not in
 * `src/Store/liveGameStore.ts`) so the pure reducers can import the types and
 * the per-slice initials without importing the zustand store itself — that
 * would be a cycle (store -> reducers -> store).
 */
import {JoinConflict} from './chess/tournamentJoin';

export const START_FEN =
  'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

export type GamePhase =
  | 'none'
  | 'starting'
  | 'playing'
  | 'evalPending'
  | 'ended';
/**
 * 'challenging' / 'waiting' are the OLD screen's names for "a challenge is out";
 * on this lane that state is `challenge.outgoing` (M2 Task B), which carries the
 * terms the cancel packet needs. Nothing sets these two — kept so the phase
 * vocabulary still matches the old screen when reading it side by side.
 */
export type LobbyPhase = 'idle' | 'seeking' | 'challenging' | 'waiting';
export type Side = 'white' | 'black';

/**
 * Where the current game state came from. 'live' = ordinary play; the three
 * server requestIds mark a restored snapshot (protocol contract: fetch /
 * reconnect / game start).
 */
export type GameOrigin = 'live' | 'On reconnect' | 'On fetch' | 'On start';

/**
 * The seek/challenge that PRODUCED this game — what the user ASKED for, not
 * how this client learned the table exists (`GameOrigin`, above, is the
 * latter: a live spawn vs a restored snapshot). `kind: 'seek'` is a random
 * `join_game_request` (tcMs/incMs/gamePlayMode as sent); `kind: 'challenge'`
 * is the `challenge_game_request` packet I sent — a Caissa game, Play &
 * Learn, a library position, or a challenge to a real user — minus the two
 * fields that do not describe the request itself. Lives on `GameSlice.request`.
 */
export type GameRequest =
  | {kind: 'seek'; tcMs: number; incMs: number; gamePlayMode: string}
  | {
      kind: 'challenge';
      packet: Omit<OutgoingChallenge, 'requestId' | 'sentAt'>;
    };

export type SessionSlice = {
  /** socket user id as a string — the profile pk (PlayChess L488). */
  me: string;
  displayName: string;
  customRules: string[];
  isStaff: boolean;
  /**
   * `gameplay_rating` from the user payload the navigator passes (PlayChess
   * reads the same field off its own profile fetch, L490). null = the payload
   * has not resolved, or the user has never been rated; the lobby's CTA reads
   * it for the Rated / Unrated line.
   */
  rating: number | null;
  /**
   * `props.isBeginnerPlayer` from Application.tsx (L1169). It hides the two
   * fastest time controls in the lobby, exactly as TimeControl L40-42 does.
   */
  isBeginner: boolean;
  /**
   * The server-side lobby A/B assignment (`user[0].fields.lobby_variant`, M2
   * spec §1.3). null = the payload does not carry one, which is every user
   * today; `resolveLobbyVariant` then falls through to its percentage bucket.
   */
  lobbyVariant: string | null;
  /**
   * The shared socket's connect/disconnect state, mirrored into the store by
   * the screen root (`useSocketConnected` reads a React context, which nothing
   * under the Stage or the Rail may touch — UX spec §6a). `Strip` shows
   * "Reconnecting…" while this is false; the clocks keep extrapolating.
   */
  connected: boolean;
  /**
   * Fix E6 (rules-gates C3, lifecycle row 62): the game is open on another
   * tab/device (the GamePlayStore `showChangeInClient` flag, mirrored into the
   * session by the screen root exactly like `connected` is). `selectBoardProps`
   * locks the board while this is true, so two tabs cannot race moves — the
   * old `ConnectionModal`'s "Activate Gameplay Here" choice is what made that
   * safe there; this lane has no such forced choice yet, so the board lock is
   * the one guarantee that cannot be skipped.
   */
  anotherTabActive: boolean;
};

/** one row of `online_players_result` (the payload is `{username: userId}`). */
export type OnlinePlayer = {userId: string; username: string};

export type LobbySlice = {
  phase: LobbyPhase;
  /** tableId handed back by join_game_response, used by leave_table_request. */
  seekTableId: number | null;
  tcMs: number;
  incMs: number;
  gamePlayMode: string;
  playerNotFound: boolean;
  /**
   * The opponent search's answer. `null` means "a search is out and no answer
   * has landed yet" (PlayChess clears `searchPlayersresult` before sending,
   * L3580-3581); an ARRAY — empty included — means the server has answered, so
   * the picker can stop its spinner and say "no match" honestly.
   */
  searchResults: OnlinePlayer[] | null;
};

export type GameResult = {
  /** idempotency key: `${tableId}:${endType}:${winner}` (protocol contract). */
  key: string;
  endType: string;
  winner: string | null;
  isDraw: boolean;
  outcome: 'Win' | 'Loss' | 'Draw' | 'Abort';
  message: string;
  myRating: number | null;
  myRatingDelta: number;
  opponentRating: number | null;
  opponentRatingDelta: number;
  /** review uuid for MY side of the game. */
  gameUuid: string;
  opponentGameUuid: string;
  pgn: string;
  moves: string[];
  finalFen: string;
  endedAt: number;
  /** PlayChess delays the modal 1.5 s on checkmate/stalemate (L3050-3055). */
  modalDelayMs: number;
};

export type RestrictedPiece = any;

export type GameSlice = {
  phase: GamePhase;
  tableId: number | null;
  whiteId: string | null;
  blackId: string | null;
  myColor: Side | null;
  opponentId: string | null;
  opponentName: string;
  opponentRating: number | null;
  myRating: number | null;
  startFen: string;
  fen: string;
  previousFen: string | null;
  isBlackTurnFirst: boolean;
  /** who moves NEXT, per the last player_turn_response. */
  turnUserId: string | null;
  /** full SAN list (deltas are expanded by the reducer). */
  moves: string[];
  whiteTimeMs: number;
  blackTimeMs: number;
  /** Date.now() when the side to move started burning time; null = paused. */
  clockRunningSince: number | null;
  tcMs: number;
  incMs: number;
  gamePlayMode: string;
  assisted: boolean;
  customRulesMap: {[userId: string]: string[]};
  /** an offer I must answer. */
  drawOffered: boolean;
  /** an offer I sent. */
  drawSent: boolean;
  drawRejectedAt: number | null;
  firstMoveDeadlineMs: number | null;
  /**
   * The LENGTH of the first-move window in seconds (20 s standard, 30 s custom
   * start position, TC/2 in a tournament/arena — PlayChess L2328-2335). Kept on
   * the slice because BLACK's window is re-armed from the same number when
   * white's first move arrives.
   */
  firstMoveSec: number;
  thinkingGateUntil: number | null;
  restrictedPiece: RestrictedPiece;
  showAbort: boolean;
  origin: GameOrigin;
  /** the seek/challenge that produced this game; see `GameRequest`. */
  request: GameRequest | null;
  /** "M + S • Rated|Unrated • Bullet/Blitz/Rapid/Classical" (L2394-2401). */
  gameDetails: string;
  readyAt: number | null;
  result: GameResult | null;
  /** set on every game_end_response — effects send end_game_confirmed. */
  endAckNeeded: boolean;
};

export type AssistedSlice = {
  hint: string;
  hint2: string;
  hintMessage: string;
  whiteWinRatio: number;
  blackWinRatio: number;
};

/**
 * A challenge I SENT and am waiting on. Every field is one the packet needs, so
 * `challenge_cancel` can be rebuilt from it without asking the lobby again
 * (PlayChess keeps the same set in its `Challenge*` states, L854-861) — and
 * `requestId` is kept because `challenge_request_expire` echoes it and an expiry
 * for somebody ELSE's challenge must not clear mine (M2 spec §4bis).
 */
export type OutgoingChallenge = {
  requestId: string;
  opponentId: string;
  opponentName: string;
  tcMs: number;
  incMs: number;
  /** the user id of the white player, or '' for a server-side toss */
  white: string;
  fen: string;
  gamePlayMode: string;
  variantId: number;
  sentAt: number;
};

/**
 * A challenge somebody sent ME. The V2 lane draws NO card for it — the
 * app-wide `Components/ChallengeComponent` owns that modal and answers it
 * (M2 spec §4bis) — but the terms are recorded so a later cancel/rematch packet
 * can be rebuilt from them.
 */
export type IncomingChallenge = {
  requestId: string;
  opponentId: string;
  opponentName: string;
  tcMs: number;
  incMs: number;
  fen: string;
  white: string;
  gamePlayMode: string;
  variantId: number;
  /**
   * NOT on the wire: `challengeGameMsgSchema` has no `tableId`, so schemapack
   * drops it and this is null on a live message (PlayChess's own
   * `setTableId(data.tableId)` at L2783 stores undefined for the same reason).
   */
  tableId: number | null;
};

/** what the lobby says after the other side answered (or I cancelled). */
export type ChallengeNotice = {
  kind: 'rejected' | 'cancelled';
  at: number;
};

export type ChallengeSlice = {
  outgoing: OutgoingChallenge | null;
  incoming: IncomingChallenge | null;
  notice: ChallengeNotice | null;
};

/**
 * Which tournament FAMILY we are in. Swiss (`Circlechess_Online` and the
 * classroom minis) and arena (`Circlechess_Arena`) share every message type on
 * this slice; only a handful of rules branch on the family, so one flag is
 * enough. The screen sets it from the details response
 * (`actions.setTournamentContext`); a running game's `gamePlayMode`
 * ('tournament' | 'arena') sets it too when it is still null, which is how a
 * reconnect straight into a tournament board learns the family (PlayChess
 * L3589-3609).
 */
export type TournamentMode = 'swiss' | 'arena';

/**
 * The join attempt's fate, in the vocabulary `TournamentPlay` already reads:
 * `code` is the old `joinTnrSuccess` (0 = nothing/transient, 1 = show the
 * failure modal with `message`, 2 = joined or a harmless duplicate) and
 * `conflict` is the overlap guard's structured answer, which arrives on its
 * OWN message type (`join_tournament_conflict`) either side of the response
 * — see `chess/tournamentJoin.ts`.
 */
export type TournamentJoinState = {
  status: 'idle' | 'pending' | 'ok' | 'failed';
  code: 0 | 1 | 2 | null;
  message: string | null;
  conflict: JoinConflict | null;
  /** the requestId of the attempt in flight; a stale response is ignored. */
  requestId: string | null;
};

/**
 * `withdraw_tournament_response` — `withDrawTournamentResMsgSchema` carries
 * `success`/`message`, unlike the old screen (which never read either and
 * treated every reply as a withdrawal). `idle` before any attempt or once a
 * join has (re)started the tournament for me; `pending` from the moment
 * `withdrawTournament()` sends, through to `ok` or `error` on the reply. See
 * reducers/tournament.ts `withdrawResponse` for the ok/error split.
 */
export type TournamentWithdrawState = {
  status: 'idle' | 'pending' | 'ok' | 'error';
  message: string | null;
};

/** `bye_response` — the round the server gave me a free point in. */
export type TournamentBye = {round: number | null; at: number | null};

/**
 * `next_round_timer_response` — the between-rounds countdown, epoch ms. Kept
 * separate from `roundStartAt` (the pairing's own 5/10 s pre-game delay,
 * cleared 11 s later and the edge that fires the round sting): overloading
 * that field would fire the sting for a between-rounds wait and then be wiped
 * by the pairing timer's unrelated 11 s clear. See reducers/tournament.ts.
 */
export type TournamentNextRound = {round: number | null; startAt: number};

export type TournamentSlice = {
  /** the tournament the surface is on, as a STRING (the wire mixes both). */
  id: string | null;
  mode: TournamentMode | null;
  /** 'generating_pairings' | 'pairings_generated' | 'tournament_finished' … */
  roundState: string | null;
  statusMessage: string | null;
  /**
   * `ongoing_games` off the pairing broadcast. A COUNT, not a list:
   * `tournamentPairingBroadcastSchema` declares it `uint32` and the old screen
   * seeds it with `useState(0)` (PlayChess L709).
   */
  ongoingGames: number | null;
  currentRound: number | null;
  /** epoch ms the next round is expected to start at; client-derived (L3216). */
  roundStartAt: number | null;
  playersJoined: number;
  ended: boolean;
  /**
   * Fix AB review (2026-10-06): the moment THIS session WITNESSED the end —
   * the end-of-tournament modal (`useRegistrationFlow.ts`) keys on it, not
   * on `ended` itself. Stamped ONLY by a LIVE end (`tournament_end`, or
   * `tournament_game_end_response` reporting `roundState ===
   * 'tournament_finished'` — the first stamp wins, never the second); a
   * reload's `tournament_status_response` and the REST-sourced
   * `adoptTournamentDetails` (fix AB) both set `ended` WITHOUT this, because
   * they are LEARNED ends, not witnessed ones — a tournament that was
   * already over before this session opened it must never pop the modal.
   */
  endedAt: number | null;
  pairingGenerated: boolean;
  join: TournamentJoinState;
  /**
   * Fix P (2026-10-06): the LAST socket verdict on my membership for THIS
   * tournament — `true` on a successful `join_tournament_response` (code 2,
   * including the harmless duplicate-join case), `false` on a successful
   * `withdraw_tournament_response`, `null` until either has happened, or
   * once `setTournamentContext` switches to a different id (or clears it).
   * The REST `is_joined` is the only truth while this is null — see
   * `useRegistrationFlow.ts`'s `joined`, which takes this OVER the REST
   * value once it is set, so a withdraw's own ack (not the request, and not
   * a REST refetch raced against the request) is what flips the button.
   */
  joinedLocal: boolean | null;
  /** fix P: bumped on both of `joinedLocal`'s transitions — the UI's own
   * refetch trigger, so a panel re-fetches on the ACK and never on the SEND. */
  membershipVersion: number;
  /** fix P: the in-flight/outcome of the LAST `withdrawTournament()` call. */
  withdraw: TournamentWithdrawState;
  bye: TournamentBye | null;
  /**
   * `tournament_starting_soon` — the server reuses it for "you are busy
   * elsewhere", so the whole payload is kept and the screen decides the copy
   * (PlayChess L2739-2756).
   */
  startingSoon: {at: number; payload: any} | null;
  /**
   * The refetch tick `TournamentPlay` watches. Bumped once per THROTTLE
   * WINDOW, never once per broadcast: game ends arrive ~1/s in a big round and
   * a bump per event was an API storm (L3428-3446). The reducer only records
   * `lastGameEndAt`; the screen root owns the 10-15 s jittered timer and calls
   * `actions.bumpGamesEnded()`.
   */
  gamesEnded: number;
  lastGameEndAt: number | null;
  /**
   * `next_round_timer_response` — the old web screen turns `remaining_ms` into
   * its own round timer (`Screens/PlayChess/index.js` L1924-1927); see
   * `nextRound` below for the decoded state. `lastEvent` itself is still just
   * recorded, so the web's parity harness keeps a handled type here without
   * either platform growing a rule the message does not need.
   */
  lastEvent: string | null;
  /** decoded `next_round_timer_response`; null once the round has started. */
  nextRound: TournamentNextRound | null;
};

/** one seat of a WATCHED game (`watchTournamentGameResMsgSchema`). */
export type WatchPlayer = {
  id: string;
  name: string;
  rating: number | null;
};

/**
 * Fix AC (2026-10-07): the game service's table state for a FINISHED game — the
 * value `watch_game_end_response` stamps and a `watch_tournament_game_response`
 * for an already-finished table arrives with (the old screen's "nothing ticks
 * at state 8"). A finished Swiss game opened over the socket within the redis
 * window carries ONLY this — no winner / endType — so it is the one signal that
 * such a watch is not live (`rail/actions/gates.ts` `liveWatch`).
 */
export const WATCH_FINISHED_STATE = 8;

export type WatchEnded = {
  endType: string;
  outcome: 'white' | 'black' | 'draw' | 'abort' | null;
  message: string;
};

export type WatchSlice = {
  active: boolean;
  /**
   * uint32 on the wire and compared against `game.tableId`, so a NUMBER here
   * (the arena guard is the only place ids are compared as strings).
   */
  tableId: number | null;
  /** the server's game state enum; 8 = finished (PlayChess L3364). */
  state: number | null;
  fen: string | null;
  prevFen: string | null;
  startFen: string | null;
  /** SAN list, expanded from the delta the way `watchMovesSanRef` does. */
  moves: string[];
  white: WatchPlayer | null;
  black: WatchPlayer | null;
  whiteTimeMs: number | null;
  blackTimeMs: number | null;
  /**
   * Date.now() of the last clock the server sent. The old watch clock DOES
   * tick locally — `PlayerComponent` hands the value to the same
   * `SimpleOptimizedClock` a played game uses, with `myTurn={currentPlayer ==
   * side}` and `playing={watchState !== 8}` (L6920-6934) — so the watched side
   * to move needs the same extrapolation anchor `game.clockRunningSince` gives.
   */
  clockRunningSince: number | null;
  /** whose turn it is in the watched game (a user id). */
  currentPlayer: string | null;
  ended: WatchEnded | null;
  /**
   * gameId replay mode: a FINISHED game opened over HTTP rather than a socket
   * watch room (PlayChess `openArenaGameByTable` L4665-4734 and the `gameId`
   * path). State only — the fetch stays platform-side.
   */
  replayGameId: string | null;
};

// ── Arena tournaments ──────────────────────────────────────────────────────
// The four types below are COPIED from the app's `src/Store/ArenaStore.ts`
// (which is itself a port of the web's), because the wire contract is shared
// and the platform bridge (`gamePlayStoreWrites.BRIDGED_ARENA_SETTERS`)
// mirrors this slice straight into that store. Keep the two in step.

/**
 * 'created' | 'running' | 'finishing' | 'finalizing' | 'finished' for `state`;
 * 'pooled' | 'paired' | 'paused' | 'idle' for `myState`. The server pushes
 * PARTIAL payloads (the finish broadcast is `{state:'finishing'}` alone), so
 * the reducer MERGES — a wholesale replace wiped myState/points/berserkEnabled
 * and hid the berserk button for a whole game (ArenaStore L110-114).
 */
export type ArenaStatus = {
  state: string;
  myState: string;
  /** pause-after-this-game: true while myState is still 'paired'. */
  paused?: boolean;
  endsAtMs: number;
  poolSize: number;
  points: number;
  streak: number;
  onFire: boolean;
  berserkEnabled: boolean;
  rank?: number;
  rating?: number;
  gamesPlayed?: number;
  total?: number;
  archived?: boolean;
};

export type ArenaPresence =
  | 'playing'
  | 'waiting'
  | 'paused'
  | 'online'
  | 'offline';

export type ArenaStandingEntry = {
  uid: string;
  username: string;
  /** GLOBAL rank (offset-aware), not the row's index in the page. */
  rank: number;
  rating?: number;
  points: number;
  streak: number;
  onFire: boolean;
  gamesPlayed: number;
  wins: number;
  longestStreak: number;
  presence?: ArenaPresence;
  scores?: number[];
};

export type ArenaCounts = {
  playing: number;
  waiting: number;
  online: number;
  offline: number;
};

export type ArenaStandings = {
  entries: ArenaStandingEntry[];
  state: string;
  endsAtMs: number;
  counts?: ArenaCounts;
  total?: number;
};

export type ArenaSlice = {
  status: ArenaStatus | null;
  standings: ArenaStandings | null;
  myBerserk: boolean;
  opponentBerserk: boolean;
  /**
   * `myBerserk` as it was when the last game ENDED — the result card needs it
   * after `resetGameBerserk` has cleared the live flags (PlayChess L4583-4587).
   */
  lastGameBerserk: boolean;
  /**
   * A refusal is 1:1 and carries an EMPTY arenaId. The old screen answers it
   * with an `Alert` (L2996-3008) / a toast on the web; a reducer cannot, so it
   * is recorded here and the screen shows it.
   */
  berserkRefusal: {reason: string; at: number} | null;
  isJoined: boolean;
  /**
   * The old `arenaAutoResumedRef` gate: a joined idle player is re-pooled at
   * most once per tournament visit (L4745-4763). Reset on an id change by
   * `actions.setTournamentContext`.
   */
  autoResumed: boolean;
};

export type MetaSlice = {
  lastMessageType: string | null;
  lastMessageAt: number | null;
  /** Date.now() of the last player_move we sent; drives the ack watchdog. */
  pendingMoveSentAt: number | null;
  lastInvalidMove: {move: string; message: string; at: number} | null;
};

export type LiveGameState = {
  session: SessionSlice;
  lobby: LobbySlice;
  game: GameSlice;
  assisted: AssistedSlice;
  challenge: ChallengeSlice;
  tournament: TournamentSlice;
  arena: ArenaSlice;
  watch: WatchSlice;
  meta: MetaSlice;
};

export const initialSession: SessionSlice = {
  me: '',
  displayName: '',
  customRules: [],
  isStaff: false,
  rating: null,
  isBeginner: false,
  lobbyVariant: null,
  // optimistic: the root writes the real value on its first effect, and a
  // false default would flash the Strip on every mount.
  connected: true,
  anotherTabActive: false,
};

export const initialLobby: LobbySlice = {
  phase: 'idle',
  seekTableId: null,
  tcMs: 180000,
  incMs: 0,
  gamePlayMode: 'Play',
  playerNotFound: false,
  searchResults: null,
};

export const initialGame: GameSlice = {
  phase: 'none',
  tableId: null,
  whiteId: null,
  blackId: null,
  myColor: null,
  opponentId: null,
  opponentName: '',
  opponentRating: null,
  myRating: null,
  startFen: START_FEN,
  fen: START_FEN,
  previousFen: null,
  isBlackTurnFirst: false,
  turnUserId: null,
  moves: [],
  whiteTimeMs: 0,
  blackTimeMs: 0,
  clockRunningSince: null,
  tcMs: 0,
  incMs: 0,
  gamePlayMode: 'Play',
  assisted: false,
  customRulesMap: {},
  drawOffered: false,
  drawSent: false,
  drawRejectedAt: null,
  firstMoveDeadlineMs: null,
  firstMoveSec: 0,
  thinkingGateUntil: null,
  restrictedPiece: '',
  showAbort: false,
  origin: 'live',
  request: null,
  gameDetails: '',
  readyAt: null,
  result: null,
  endAckNeeded: false,
};

export const initialAssisted: AssistedSlice = {
  hint: '',
  hint2: '',
  hintMessage: '',
  whiteWinRatio: 0.5,
  blackWinRatio: 0.5,
};

export const initialChallenge: ChallengeSlice = {
  outgoing: null,
  incoming: null,
  notice: null,
};

export const initialTournamentJoin: TournamentJoinState = {
  status: 'idle',
  code: null,
  message: null,
  conflict: null,
  requestId: null,
};

export const initialTournamentWithdraw: TournamentWithdrawState = {
  status: 'idle',
  message: null,
};

export const initialTournament: TournamentSlice = {
  id: null,
  mode: null,
  roundState: null,
  statusMessage: null,
  ongoingGames: null,
  currentRound: null,
  roundStartAt: null,
  playersJoined: 0,
  ended: false,
  endedAt: null,
  pairingGenerated: false,
  join: initialTournamentJoin,
  joinedLocal: null,
  membershipVersion: 0,
  withdraw: initialTournamentWithdraw,
  bye: null,
  startingSoon: null,
  gamesEnded: 0,
  lastGameEndAt: null,
  lastEvent: null,
  nextRound: null,
};

export const initialArena: ArenaSlice = {
  status: null,
  standings: null,
  myBerserk: false,
  opponentBerserk: false,
  lastGameBerserk: false,
  berserkRefusal: null,
  isJoined: false,
  autoResumed: false,
};

export const initialWatch: WatchSlice = {
  active: false,
  tableId: null,
  state: null,
  fen: null,
  prevFen: null,
  startFen: null,
  moves: [],
  white: null,
  black: null,
  whiteTimeMs: null,
  blackTimeMs: null,
  clockRunningSince: null,
  currentPlayer: null,
  ended: null,
  replayGameId: null,
};

export const initialMeta: MetaSlice = {
  lastMessageType: null,
  lastMessageAt: null,
  pendingMoveSentAt: null,
  lastInvalidMove: null,
};

export const initialState: LiveGameState = {
  session: initialSession,
  lobby: initialLobby,
  game: initialGame,
  assisted: initialAssisted,
  challenge: initialChallenge,
  tournament: initialTournament,
  arena: initialArena,
  watch: initialWatch,
  meta: initialMeta,
};

/** Server fields mix uint32 and string for the same id — compare as strings. */
export const id = (v: any): string =>
  v === undefined || v === null || v === '' ? '' : String(v);
